import {
  hasUploadFailed,
  isStoryCompressedVideoReady,
  isStoryOriginalVideoReady,
  isStoryReady,
  isStoryTranscriptionReady,
  isStoryWaveformReady,
} from './utils';

import type { StoryUploadPhase } from '@src/stores-v2/UserStoryUploadFlowStore';
import type { DashboardStory } from '@src/types.ts/story';

export interface UploadStepInfo {
  stepNumber: number; // 1-based, 6 = complete/final
  totalSteps: number;
  label: string; // short
  detail: string; // longer description
  done: boolean;
  error?: boolean;
}

// Total logical steps (excluding failed): 5 actionable + complete state
const TOTAL_STEPS = 5;

export function deriveUploadStep(
  story: DashboardStory,
  phase: StoryUploadPhase,
  /** PLAT-12104: progress of the in-browser scene/face detection, while it runs. */
  clientVideoAnalysisProgress?: number,
): UploadStepInfo {
  if (hasUploadFailed(story)) {
    return {
      stepNumber: 0,
      totalSteps: TOTAL_STEPS,
      label: 'Failed',
      detail: 'Upload failed – please retry',
      done: false,
      error: true,
    };
  }

  // The transcript is ready but the story stays closed until the browser's
  // scene/face detection ends — say so instead of a step it already passed.
  if (
    clientVideoAnalysisProgress !== undefined &&
    isStoryTranscriptionReady(story)
  ) {
    return {
      stepNumber: 3,
      totalSteps: TOTAL_STEPS,
      label: 'Analyzing Video',
      detail: `Detecting scenes and faces · ${Math.round(
        clientVideoAnalysisProgress,
      )}%`,
      done: false,
    };
  }

  // "Complete" requires all four readiness signals including waveform.
  // We deliberately do NOT key off isStoryLoading here: that helper marks a
  // story as "loaded enough to open" once the three core signals land, but
  // we still want to show the "Building Waveform" step until the waveform
  // truly finishes.
  if (isStoryReady(story) && isStoryWaveformReady(story)) {
    return {
      stepNumber: TOTAL_STEPS,
      totalSteps: TOTAL_STEPS,
      label: 'Complete',
      detail: 'Ready',
      done: true,
    };
  }

  if (phase.phase === 'idle') {
    return {
      stepNumber: 0,
      totalSteps: TOTAL_STEPS,
      label: 'Pending',
      detail: 'Waiting to start',
      done: false,
    };
  }

  // Phase: waiting in upload queue behind another upload
  if (phase.phase === 'queued') {
    return {
      stepNumber: 0,
      totalSteps: TOTAL_STEPS,
      label: 'Queued',
      detail: 'Waiting for another upload to finish',
      done: false,
    };
  }

  // Phase: uploading video file(s)
  if (phase.phase === 'uploading') {
    return {
      stepNumber: 1,
      totalSteps: TOTAL_STEPS,
      label: 'Uploading Video',
      detail: 'Transferring video file(s)',
      done: false,
    };
  }
  // From here on we are processing. Once the original video is optimized,
  // compression / waveform / transcription run in parallel on the backend —
  // we order the labels by typical duration (transcription is usually the
  // longest) so the user sees the most informative step for the most time.
  if (!isStoryOriginalVideoReady(story)) {
    return {
      stepNumber: 2,
      totalSteps: TOTAL_STEPS,
      label: 'Optimizing',
      detail: 'Optimizing original video',
      done: false,
    };
  }
  if (!isStoryTranscriptionReady(story)) {
    return {
      stepNumber: 3,
      totalSteps: TOTAL_STEPS,
      label: 'Transcribing',
      detail: 'Speech to text in progress',
      done: false,
    };
  }
  if (!isStoryCompressedVideoReady(story)) {
    return {
      stepNumber: 4,
      totalSteps: TOTAL_STEPS,
      label: 'Versioning',
      detail: 'Creating compressed video derivatives',
      done: false,
    };
  }
  if (!isStoryWaveformReady(story)) {
    return {
      stepNumber: 5,
      totalSteps: TOTAL_STEPS,
      label: 'Building Waveform',
      detail: 'Generating audio waveform',
      done: false,
    };
  }
  // All ready but still considered loading (rare race)
  return {
    stepNumber: TOTAL_STEPS,
    totalSteps: TOTAL_STEPS,
    label: 'Finalizing',
    detail: 'Finishing setup',
    done: false,
  };
}
